import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import * as Icons from "lucide-react";
import { ArrowRight, ArrowLeft, ArrowUpRight, Check, Sparkles, Download, Lock, MessageCircle } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { QUESTIONS, PACKAGES, BUILD_GOALS, recommendPackage } from "../lib/quote";
import CTASection from "../components/CTASection";
import { FAQS } from "../lib/data";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import {
  getSavedRegion,
  getCurrencyForRegion,
  formatPrice,
} from "../lib/currency";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Web3Forms — access key is safe to expose client-side (that's how Web3Forms is designed to work),
// but it's pulled from an env var first so you can override it per-environment without editing code.
const WEB3FORMS_ACCESS_KEY = process.env.REACT_APP_WEB3FORMS_ACCESS_KEY || "06295cd3-8316-435d-b59d-6509228f7e55";
const WEB3FORMS_ENDPOINT = "https://api.web3forms.com/submit";

const Icon = ({ name, className }) => {
  const Cmp = Icons[name] || Icons.Sparkles;
  return <Cmp className={className} aria-hidden="true" />;
};

// Full quiz (all 6 questions) — used for Website Design / Website Redesign.
const TOTAL_QUESTIONS = QUESTIONS.length;
// Index of the last question shared by every path (business_type, has_website, main_goal).
const BRANCH_STEP_INDEX = QUESTIONS.findIndex((q) => q.id === "main_goal");

async function postToWeb3Forms(payload) {
  const res = await fetch(WEB3FORMS_ENDPOINT, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(payload),
  });
  const data = await res.json();
  if (!data.success) {
    throw new Error(data.message || "Web3Forms submission failed");
  }
  return data;
}

// Turns the raw answers object into a readable block for the email body,
// and also flattens it into individual named fields so they show as separate
// rows in the Web3Forms notification email.
function buildWeb3FormsPayload(answers, lead, pkg, currency, region) {
  const formattedAnswers = [
    `Business Type: ${answers.business_type || "-"}`,
    `Existing Website: ${answers.has_website || "-"}`,
    `Main Goal: ${answers.main_goal || "-"}`,
    `Page Count: ${answers.page_count || "-"}`,
    `Timeline: ${answers.timeline || "-"}`,
    `Budget: ${answers.budget || "-"}`,
  ].join("\n");

  return {
    access_key: WEB3FORMS_ACCESS_KEY,
    subject: `New Quote Request — ${pkg.name} (${lead.first_name || "Unnamed"})`,
    from_name: "SolkSites Quote Form",
    botcheck: "",

    name: lead.first_name,
    email: lead.email,
    phone: lead.phone || "Not provided",
    business_name: lead.business_name || "Not provided",

    recommended_package: pkg.name,
    estimated_price: formatPrice(pkg.prices[currency], currency),

    business_type: answers.business_type,
    has_website: answers.has_website,
    main_goal: answers.main_goal,
    page_count: answers.page_count,
    timeline: answers.timeline,
    budget: answers.budget,

    message: `New quote request from ${lead.first_name} (${lead.email}).

    Recommended package: ${pkg.name} (${formatPrice(
          pkg.prices[currency],
          currency
        )})

    Currency: ${currency}
    Region: ${region}

    ${formattedAnswers}`,
  };
}

// Lightweight payload for Care Plans / Domain Management / Custom Email Setup —
// no package, no page/timeline/budget, just the service requested plus a free-text note.
function buildServiceWeb3FormsPayload(answers, lead, serviceNote) {
  return {
    access_key: WEB3FORMS_ACCESS_KEY,
    subject: `New Service Request — ${answers.main_goal} (${lead.first_name || "Unnamed"})`,
    from_name: "SolkSites Quote Form",
    botcheck: "",

    name: lead.first_name,
    email: lead.email,
    phone: lead.phone || "Not provided",
    business_name: lead.business_name || "Not provided",

    requested_service: answers.main_goal,
    business_type: answers.business_type,
    has_website: answers.has_website,
    details: serviceNote || "No additional details provided",

    message: `New ${answers.main_goal} request from ${lead.first_name} (${lead.email}).\n\nBusiness type: ${answers.business_type || "-"}\nExisting website: ${answers.has_website || "-"}\n\nDetails:\n${serviceNote || "No additional details provided"}`,
  };
}

export default function Pricing() {
  const [stage, setStage] = useState("intro");
  const [region, setRegion] = useState(getSavedRegion());
  const currency = getCurrencyForRegion(region);
  const [step, setStep] = useState(0); // question index
  const [answers, setAnswers] = useState({
    business_type: null, has_website: null, main_goal: null,
    page_count: null, timeline: null, budget: null,
  });
  const [lead, setLead] = useState({ first_name: "", email: "", phone: "", business_name: "" });
  const [serviceNote, setServiceNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    const handleRegionChange = (event) => {
      setRegion(event.detail.region);
    };

    window.addEventListener(
      "solksites-region-change",
      handleRegionChange
    );

    return () => {
      window.removeEventListener(
        "solksites-region-change",
        handleRegionChange
      );
    };
  }, []);

  const isBuildGoal = (goal) => BUILD_GOALS.includes(goal);

  const progress = useMemo(() => {
    if (stage === "intro") return 0;
    if (stage === "capture" || stage === "service_capture") return 92;
    if (stage === "result" || stage === "service_result") return 100;
    return Math.round(((step + 1) / (TOTAL_QUESTIONS + 1)) * 92);
  }, [stage, step]);

  const currentQuestion = QUESTIONS[step];

  const start = () => { setStage("question"); setStep(0); };

  const back = () => {
    if (stage === "capture") { setStage("question"); setStep(TOTAL_QUESTIONS - 1); return; }
    if (stage === "service_capture") { setStage("question"); setStep(BRANCH_STEP_INDEX); return; }
    if (step === 0) { setStage("intro"); return; }
    setStep(step - 1);
  };

  const answerSingle = (val) => {
    const key = currentQuestion.id;
    setAnswers((a) => ({ ...a, [key]: val }));

    setTimeout(() => {
      // Branch point: after main_goal, either continue the full quiz
      // or jump straight to the lightweight service capture form.
      if (key === "main_goal") {
        if (isBuildGoal(val)) {
          setStep(step + 1);
        } else {
          setStage("service_capture");
        }
        return;
      }
      if (step < TOTAL_QUESTIONS - 1) setStep(step + 1);
      else setStage("capture");
    }, 250);
  };

  // ===== Full build quiz submission (Website Design / Website Redesign) =====
  const submitLead = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);

    const rec = recommendPackage(answers, currency);
    const pkg = PACKAGES[rec.key];
    console.log("DEBUG:", { rec, pkg, currency });

    const estimatedPrice = pkg.prices[currency];
    const formattedPrice = formatPrice(estimatedPrice, currency);

    const backendPromise = axios.post(`${API}/quote`, {
      ...answers,
      ...lead,
      recommended_package: pkg.name,
      estimated_price: estimatedPrice,
      currency,
      region,
    });
    const web3formsPromise = postToWeb3Forms(buildWeb3FormsPayload(answers, lead, pkg, currency, region));

    const [backendResult, web3formsResult] = await Promise.allSettled([backendPromise, web3formsPromise]);

    if (backendResult.status === "rejected") console.error("Backend save failed:", backendResult.reason);
    if (web3formsResult.status === "rejected") console.error("Web3Forms email failed:", web3formsResult.reason);

    if (backendResult.status === "fulfilled" || web3formsResult.status === "fulfilled") {
      setResult({ ...rec, pkg });
      setStage("result");
      toast.success("Your personalised quote is ready.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      toast.error("Something went wrong saving your quote. Please try again or contact us directly.");
    }
    setSubmitting(false);
  };

  // ===== Lightweight service request submission (Care Plans / Domain / Email) =====
  const submitServiceRequest = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);

    const backendPromise = axios.post(`${API}/quote`, {
      ...answers,
      ...lead,
      service_request: true,
      requested_service: answers.main_goal,
      details: serviceNote,
    });
    const web3formsPromise = postToWeb3Forms(buildServiceWeb3FormsPayload(answers, lead, serviceNote));

    const [backendResult, web3formsResult] = await Promise.allSettled([backendPromise, web3formsPromise]);

    if (backendResult.status === "rejected") console.error("Backend save failed:", backendResult.reason);
    if (web3formsResult.status === "rejected") console.error("Web3Forms email failed:", web3formsResult.reason);

    if (backendResult.status === "fulfilled" || web3formsResult.status === "fulfilled") {
      setStage("service_result");
      toast.success("Your request has been sent.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      toast.error("Something went wrong sending your request. Please try again or contact us directly.");
    }
    setSubmitting(false);
  };

  const restart = () => {
    setAnswers({ business_type: null, has_website: null, main_goal: null, page_count: null, timeline: null, budget: null });
    setLead({ first_name: "", email: "", phone: "", business_name: "" });
    setServiceNote("");
    setResult(null);
    setStep(0);
    setStage("intro");
  };

  return (
    <>
      {/* PROGRESS BAR (visible only during flow) */}
      {stage !== "intro" && (
        <div className="fixed top-0 inset-x-0 z-40 h-1 bg-slate-100 dark:bg-slate-900" aria-hidden="true">
          <motion.div
            className="h-full bg-blue-600"
            initial={false}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          />
        </div>
      )}

      <AnimatePresence mode="wait">
        {/* ===== INTRO ===== */}
        {stage === "intro" && (
          <motion.section
            key="intro"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="relative overflow-hidden min-h-[80vh] flex items-center py-24"
            data-testid="quote-intro"
          >
            <div className="absolute inset-0 subtle-radial" aria-hidden="true" />
            <div className="absolute inset-0 grid-lines-bg opacity-50" aria-hidden="true" />
            <div className="container-page relative text-center">
              <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}>
                <div className="inline-flex items-center gap-2 badge-pill mb-8">
                  <Sparkles className="h-3.5 w-3.5" /> Personalised in under 60 seconds
                </div>
                <h1 className="text-5xl md:text-7xl lg:text-[88px] font-extrabold tracking-[-0.03em] leading-[1.02] text-slate-900 dark:text-white max-w-4xl mx-auto">
                  Get Your <span className="text-blue-600 dark:text-blue-400">Personalised Quote</span>
                </h1>
                <p className="mt-8 text-lg md:text-xl text-slate-600 dark:text-slate-300 max-w-2xl mx-auto leading-relaxed">
                  Answer a few quick questions and we'll recommend the perfect option for your business in under 60 seconds.
                </p>
                <div className="mt-12">
                  <button onClick={start} className="btn-primary text-base px-10 py-4" data-testid="quote-start-button">
                    Get My Price <ArrowRight className="h-4 w-4" />
                  </button>
                </div>
                <p className="mt-6 text-sm text-slate-500 dark:text-slate-400 inline-flex items-center gap-1.5">
                  <Lock className="h-3.5 w-3.5" /> A few quick questions · Free · No email required to start
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.7 }}
                className="mt-20 grid gap-4 md:grid-cols-3 max-w-3xl mx-auto"
              >
                {[
                  { icon: "Sparkles", title: "Personalised", desc: "We match a recommendation to your answers, not the other way around." },
                  { icon: "Zap", title: "Under a minute", desc: "A few quick taps. No jargon, no long forms." },
                  { icon: "Lock", title: "No spam", desc: "You'll get one calm email — that's it." },
                ].map((f) => (
                  <div key={f.title} className="card-surface p-6 text-left">
                    <div className="h-10 w-10 rounded-xl bg-blue-50 dark:bg-blue-500/10 flex items-center justify-center text-blue-600 dark:text-blue-400">
                      <Icon name={f.icon} className="h-4 w-4" />
                    </div>
                    <h3 className="mt-4 font-semibold text-slate-900 dark:text-white">{f.title}</h3>
                    <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">{f.desc}</p>
                  </div>
                ))}
              </motion.div>
            </div>
          </motion.section>
        )}

        {/* ===== QUESTIONS ===== */}
        {stage === "question" && currentQuestion && (
          <motion.section
            key={`q-${step}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="min-h-[80vh] py-16 md:py-24"
            data-testid={`quote-question-${step}`}
          >
            <div className="container-page max-w-4xl">
              <div className="flex items-center justify-between mb-10">
                <button onClick={back} className="btn-ghost" data-testid="quote-back">
                  <ArrowLeft className="h-4 w-4" /> Back
                </button>
                <span className="text-sm font-semibold text-slate-500 dark:text-slate-400">
                  Question {step + 1} of {TOTAL_QUESTIONS}
                </span>
              </div>

              <motion.div
                key={`title-${step}`}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.05, duration: 0.4 }}
              >
                <h2 className="text-4xl md:text-5xl font-extrabold tracking-tighter text-slate-900 dark:text-white leading-[1.05]">
                  {currentQuestion.title}
                </h2>
                <p className="mt-4 text-lg text-slate-600 dark:text-slate-300">{currentQuestion.subtitle}</p>
              </motion.div>

              <div className={`mt-12 grid gap-4 ${currentQuestion.options.length > 6 ? "sm:grid-cols-2 lg:grid-cols-3" : currentQuestion.options.length > 4 ? "sm:grid-cols-2 lg:grid-cols-3" : "sm:grid-cols-2"}`}>
                {currentQuestion.options.map((opt, i) => {
                  const selected = answers[currentQuestion.id] === opt.value;
                  return (
                    <motion.button
                      key={opt.value}
                      type="button"
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.05 + i * 0.04, duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                      whileHover={{ y: -3 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => answerSingle(opt.value)}
                      data-testid={`quote-option-${currentQuestion.id}-${opt.value.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                      className={`text-left p-6 rounded-[20px] border-2 transition-all bg-white dark:bg-slate-900/60 ${selected
                          ? "border-blue-600 shadow-[0_16px_40px_-16px_rgba(37,99,235,0.35)]"
                          : "border-slate-100 dark:border-slate-800 hover:border-blue-200 dark:hover:border-blue-500/40 hover:shadow-lg"
                        }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className={`h-11 w-11 rounded-xl flex items-center justify-center transition-colors ${selected ? "bg-blue-600 text-white" : "bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400"}`}>
                          <Icon name={opt.icon} className="h-5 w-5" />
                        </div>
                        {selected && (
                          <span className="h-6 w-6 rounded-full bg-blue-600 text-white flex items-center justify-center flex-none">
                            <Check className="h-3.5 w-3.5" />
                          </span>
                        )}
                      </div>
                      <p className="mt-5 font-semibold text-slate-900 dark:text-white">{typeof opt.label === "string" ? opt.label : opt.label?.[currency] || opt.value}</p>
                      {opt.desc && <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{opt.desc}</p>}
                    </motion.button>
                  );
                })}
              </div>
            </div>
          </motion.section>
        )}

        {/* ===== LEAD CAPTURE (build path: Website Design / Redesign) ===== */}
        {stage === "capture" && (
          <motion.section
            key="capture"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.4 }}
            className="min-h-[80vh] py-16 md:py-24"
            data-testid="quote-capture"
          >
            <div className="container-page max-w-2xl">
              <button onClick={back} className="btn-ghost mb-10" data-testid="quote-capture-back">
                <ArrowLeft className="h-4 w-4" /> Back
              </button>

              <div className="text-center mb-10">
                <div className="mx-auto h-14 w-14 rounded-full bg-blue-50 dark:bg-blue-500/10 flex items-center justify-center text-blue-600 dark:text-blue-400 mb-6">
                  <Sparkles className="h-6 w-6" />
                </div>
                <h2 className="text-4xl md:text-5xl font-extrabold tracking-tighter text-slate-900 dark:text-white">
                  Your personalised quote is ready
                </h2>
                <p className="mt-4 text-lg text-slate-600 dark:text-slate-300">
                  Enter your details below to unlock your personalised recommendation and receive a copy by email.
                </p>
              </div>

              <form onSubmit={submitLead} className="card-surface p-8 md:p-10 space-y-5" data-testid="quote-lead-form">
                <label className="block">
                  <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">First name</span>
                  <input required value={lead.first_name} onChange={(e) => setLead({ ...lead, first_name: e.target.value })} type="text" className="mt-2 w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50 px-4 py-3.5 text-sm focus:border-blue-600 focus:bg-white dark:focus:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-100" data-testid="quote-first-name" />
                </label>
                <label className="block">
                  <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">Email address</span>
                  <input required value={lead.email} onChange={(e) => setLead({ ...lead, email: e.target.value })} type="email" className="mt-2 w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50 px-4 py-3.5 text-sm focus:border-blue-600 focus:bg-white dark:focus:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-100" data-testid="quote-email" />
                </label>
                <label className="block">
                  <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">Phone <span className="text-slate-400 font-normal">(optional, but encouraged)</span></span>
                  <input value={lead.phone} onChange={(e) => setLead({ ...lead, phone: e.target.value })} type="tel" className="mt-2 w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50 px-4 py-3.5 text-sm focus:border-blue-600 focus:bg-white dark:focus:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-100" data-testid="quote-phone" />
                </label>
                <label className="block">
                  <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">Business name <span className="text-slate-400 font-normal">(optional)</span></span>
                  <input value={lead.business_name} onChange={(e) => setLead({ ...lead, business_name: e.target.value })} type="text" className="mt-2 w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50 px-4 py-3.5 text-sm focus:border-blue-600 focus:bg-white dark:focus:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-100" data-testid="quote-business-name" />
                </label>
                <button type="submit" disabled={submitting} className="btn-primary w-full text-base py-4 disabled:opacity-60" data-testid="quote-submit">
                  {submitting ? "Preparing your quote…" : "Show my quote"} <ArrowRight className="h-4 w-4" />
                </button>
                <p className="text-xs text-slate-500 dark:text-slate-400 text-center inline-flex items-center gap-1.5 justify-center w-full">
                  <Lock className="h-3 w-3" /> We never share your details. Unsubscribe any time.
                </p>
              </form>
            </div>
          </motion.section>
        )}

        {/* ===== SERVICE CAPTURE (Care Plans / Domain Management / Custom Email Setup) ===== */}
        {stage === "service_capture" && (
          <motion.section
            key="service_capture"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.4 }}
            className="min-h-[80vh] py-16 md:py-24"
            data-testid="quote-service-capture"
          >
            <div className="container-page max-w-2xl">
              <button onClick={back} className="btn-ghost mb-10" data-testid="quote-service-capture-back">
                <ArrowLeft className="h-4 w-4" /> Back
              </button>

              <div className="text-center mb-10">
                <div className="mx-auto h-14 w-14 rounded-full bg-blue-50 dark:bg-blue-500/10 flex items-center justify-center text-blue-600 dark:text-blue-400 mb-6">
                  <MessageCircle className="h-6 w-6" />
                </div>
                <h2 className="text-4xl md:text-5xl font-extrabold tracking-tighter text-slate-900 dark:text-white">
                  Tell us a bit more
                </h2>
                <p className="mt-4 text-lg text-slate-600 dark:text-slate-300">
                  {answers.main_goal} isn't a one-size-fits-all price, so leave your details and a quick note and we'll come back with options that fit.
                </p>
              </div>

              <form onSubmit={submitServiceRequest} className="card-surface p-8 md:p-10 space-y-5" data-testid="quote-service-form">
                <label className="block">
                  <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">First name</span>
                  <input required value={lead.first_name} onChange={(e) => setLead({ ...lead, first_name: e.target.value })} type="text" className="mt-2 w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50 px-4 py-3.5 text-sm focus:border-blue-600 focus:bg-white dark:focus:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-100" data-testid="service-first-name" />
                </label>
                <label className="block">
                  <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">Email address</span>
                  <input required value={lead.email} onChange={(e) => setLead({ ...lead, email: e.target.value })} type="email" className="mt-2 w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50 px-4 py-3.5 text-sm focus:border-blue-600 focus:bg-white dark:focus:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-100" data-testid="service-email" />
                </label>
                <label className="block">
                  <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">Phone <span className="text-slate-400 font-normal">(optional)</span></span>
                  <input value={lead.phone} onChange={(e) => setLead({ ...lead, phone: e.target.value })} type="tel" className="mt-2 w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50 px-4 py-3.5 text-sm focus:border-blue-600 focus:bg-white dark:focus:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-100" data-testid="service-phone" />
                </label>
                <label className="block">
                  <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">Business name <span className="text-slate-400 font-normal">(optional)</span></span>
                  <input value={lead.business_name} onChange={(e) => setLead({ ...lead, business_name: e.target.value })} type="text" className="mt-2 w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50 px-4 py-3.5 text-sm focus:border-blue-600 focus:bg-white dark:focus:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-100" data-testid="service-business-name" />
                </label>
                <label className="block">
                  <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">What do you need? <span className="text-slate-400 font-normal">(optional)</span></span>
                  <textarea
                    value={serviceNote}
                    onChange={(e) => setServiceNote(e.target.value)}
                    rows={4}
                    placeholder="e.g. which plan interests you (Essential/Business/Premium), how many pages your site has, how often you'd expect to need changes..."
                    className="mt-2 w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50 px-4 py-3.5 text-sm focus:border-blue-600 focus:bg-white dark:focus:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-100"
                    data-testid="service-note"
                  />
                </label>
                <button type="submit" disabled={submitting} className="btn-primary w-full text-base py-4 disabled:opacity-60" data-testid="quote-service-submit">
                  {submitting ? "Sending…" : "Send my request"} <ArrowRight className="h-4 w-4" />
                </button>
                <p className="text-xs text-slate-500 dark:text-slate-400 text-center inline-flex items-center gap-1.5 justify-center w-full">
                  <Lock className="h-3 w-3" /> We never share your details. Unsubscribe any time.
                </p>
              </form>
            </div>
          </motion.section>
        )}

        {/* ===== RESULT (build path) ===== */}
        {stage === "result" && result && (
          <motion.section
            key="result"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="py-16 md:py-24"
            data-testid="quote-result"
          >
            <div className="container-page max-w-5xl">
              <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: 0.1, duration: 0.5, type: "spring", stiffness: 120 }} className="text-center mb-12">
                <div className="inline-flex items-center gap-2 badge-pill mb-5">
                  <Check className="h-3.5 w-3.5" /> Your personalised recommendation
                </div>
                <p className="eyebrow">We recommend</p>
                <h1 className="mt-4 text-5xl md:text-7xl font-extrabold tracking-tighter text-slate-900 dark:text-white">
                  {result.pkg.name}
                </h1>
              </motion.div>

              {/* Main recommended card */}
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.6 }}
                className="rounded-[28px] bg-slate-950 text-white p-8 md:p-12 relative overflow-hidden shadow-2xl"
                data-testid="quote-recommended-card"
              >
                <div className="absolute -top-24 -right-24 h-80 w-80 rounded-full bg-blue-600/25 blur-3xl" aria-hidden="true" />
                <div className="absolute inset-0 grid-lines-bg opacity-30" aria-hidden="true" />

                <div className="relative grid md:grid-cols-2 gap-10">
                  <div>
                    <p className="text-blue-400 text-sm font-semibold tracking-widest">ESTIMATED PRICE</p>
                    <p className="mt-3 text-6xl md:text-7xl font-extrabold tracking-tighter">{formatPrice(result.pkg.prices[currency], currency)}</p>
                    <div className="mt-8 space-y-4">
                      <div>
                        <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Timeline</p>
                        <p className="mt-1 text-lg font-semibold text-white">{result.pkg.timeline}</p>
                      </div>
                      <div>
                        <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Best for</p>
                        <p className="mt-1 text-slate-200">{result.pkg.bestFor}</p>
                      </div>
                    </div>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">What's included</p>
                    <ul className="mt-4 space-y-3">
                      {result.pkg.included.map((f) => (
                        <li key={f} className="flex items-start gap-2.5">
                          <Check className="h-4 w-4 text-blue-400 mt-1 flex-none" />
                          <span className="text-slate-200 text-sm">{f}</span>
                        </li>
                      ))}
                    </ul>
                    {result.pkg.optional?.length > 0 && (
                      <>
                        <p className="mt-6 text-xs uppercase tracking-widest text-slate-500 font-semibold">Optional add-ons</p>
                        <div className="mt-3 flex flex-wrap gap-2">
                          {result.pkg.optional.map((o) => (
                            <span key={o} className="badge-pill bg-white/5 text-slate-300 ring-white/10">{o}</span>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </div>

                <div className="relative mt-10 pt-8 border-t border-white/10">
                  <p className="text-slate-300 text-sm md:text-base leading-relaxed italic max-w-3xl">
                    "{result.reason}"
                  </p>
                </div>

                <div className="relative mt-10 flex flex-col sm:flex-row gap-3">
                  <Link to="/contact" className="btn-primary bg-white text-slate-900 hover:bg-slate-100 hover:text-slate-900" data-testid="result-book-cta">
                    Book my free discovery call <ArrowUpRight className="h-4 w-4" />
                  </Link>
                  <button
                    type="button"
                    onClick={() => window.print()}
                    className="inline-flex items-center justify-center gap-2 bg-white/5 border border-white/15 text-white rounded-full px-7 py-3.5 text-sm font-semibold hover:bg-white/10 transition-colors"
                    data-testid="result-download-cta"
                  >
                    <Download className="h-4 w-4" /> Download my quote
                  </button>
                </div>
              </motion.div>

              {/* Alternatives */}
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4, duration: 0.5 }} className="mt-16">
                <p className="text-sm text-slate-500 dark:text-slate-400 text-center">Other packages that may also suit you</p>
                <div className="mt-6 grid gap-6 md:grid-cols-2">
                  {result.alternatives.map((altKey) => {
                    const alt = PACKAGES[altKey];
                    return (
                      <div key={alt.key} className="card-surface p-6" data-testid={`alt-package-${alt.key}`}>
                        <p className="text-sm font-semibold text-slate-500 dark:text-slate-400">{alt.name}</p>
                        <p className="mt-2 text-3xl font-extrabold tracking-tighter text-slate-900 dark:text-white">{formatPrice(alt.prices[currency], currency)}</p>
                        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{alt.timeline}</p>
                        <p className="mt-4 text-sm text-slate-600 dark:text-slate-400">{alt.bestFor}</p>
                      </div>
                    );
                  })}
                </div>
              </motion.div>

              <div className="mt-16 text-center">
                <button onClick={restart} className="btn-ghost" data-testid="quote-restart">
                  ← Start over with different answers
                </button>
              </div>
            </div>
          </motion.section>
        )}

        {/* ===== SERVICE RESULT (Care Plans / Domain Management / Custom Email Setup) ===== */}
        {stage === "service_result" && (
          <motion.section
            key="service_result"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="min-h-[70vh] flex items-center py-16 md:py-24"
            data-testid="quote-service-result"
          >
            <div className="container-page max-w-2xl text-center">
              <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: 0.1, duration: 0.5, type: "spring", stiffness: 120 }}>
                <div className="mx-auto h-16 w-16 rounded-full bg-blue-50 dark:bg-blue-500/10 flex items-center justify-center text-blue-600 dark:text-blue-400 mb-6">
                  <Check className="h-7 w-7" />
                </div>
                <h1 className="text-4xl md:text-5xl font-extrabold tracking-tighter text-slate-900 dark:text-white">
                  Thanks — your request is in
                </h1>
                <p className="mt-5 text-lg text-slate-600 dark:text-slate-300">
                  We've received your {answers.main_goal.toLowerCase()} request and will be in touch by email shortly with options and pricing tailored to what you need.
                </p>
                <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
                  <Link to="/contact" className="btn-primary" data-testid="service-result-contact-cta">
                    Book a free discovery call <ArrowUpRight className="h-4 w-4" />
                  </Link>
                  <button onClick={restart} className="btn-ghost" data-testid="service-result-restart">
                    ← Start over
                  </button>
                </div>
              </motion.div>
            </div>
          </motion.section>
        )}
      </AnimatePresence>

      {/* FAQ — only visible on intro & either result screen */}
      {(stage === "intro" || stage === "result" || stage === "service_result") && (
        <section className="section" data-testid="pricing-faq">
          <div className="container-page max-w-3xl">
            <p className="eyebrow text-center">Pricing questions</p>
            <h2 className="mt-4 text-3xl md:text-4xl font-extrabold tracking-tighter text-slate-900 dark:text-white text-center">Common questions</h2>
            <div className="mt-10">
              <Accordion type="single" collapsible className="space-y-3">
                {FAQS.filter((f) => f.category === "Pricing" || f.category === "Timeline").map((f, i) => (
                  <AccordionItem key={i} value={`item-${i}`} className="card-surface px-6 border">
                    <AccordionTrigger className="text-left py-5 hover:no-underline font-semibold text-slate-900 dark:text-white">{f.q}</AccordionTrigger>
                    <AccordionContent className="text-slate-600 dark:text-slate-400 pb-5 leading-relaxed">{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </section>
      )}
    </>
  );
}